import type { MessageKey } from "@colorglo/i18n";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
} from "@colorglo/ui/components";
import { hqNotificationIcons, iconSize, NavShopOrders } from "@colorglo/ui/icons";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  type HqNotificationFeed,
  useHqNotifications,
  useMarkHqNotificationsRead,
} from "../../api/shop.ts";
import { useNotificationPreferences } from "../../api/staff-notifications.ts";
import { useT } from "../../lib/locale.ts";
import { armSound, playChime, playOrderChime } from "../../lib/sound.ts";
import { formatInstant, HQ_TIMEZONE } from "./format.ts";

type HqEntry = HqNotificationFeed["data"][number];

export function HqBell({ enabled }: { readonly enabled: boolean }) {
  const t = useT();
  const feed = useHqNotifications(enabled);
  const markRead = useMarkHqNotificationsRead();
  const [open, setOpen] = useState(false);

  useOrderChime(feed.data?.data ?? [], feed.isSuccess);

  if (!enabled) return null;

  const unread = feed.data?.unread_count ?? 0;
  const entries = feed.data?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread > 0 ? `${t("shop.hq_bell.open")} (${unread})` : t("shop.hq_bell.open")}
        >
          <NavShopOrders {...iconSize.control} aria-hidden="true" />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-brand-strong px-1 text-[10px] font-medium leading-4 text-on-brand"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between gap-2 p-3">
          <p className="text-body-sm font-medium">{t("shop.hq_bell.title")}</p>
          {unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate([])}
            >
              {t("shop.hq_bell.mark_all_read")}
            </Button>
          )}
        </div>
        <Separator />
        {entries.length === 0 ? (
          <p className="p-6 text-center text-body-sm text-muted-foreground">
            {t("shop.hq_bell.empty")}
          </p>
        ) : (
          <ScrollArea className="max-h-96">
            <ul className="divide-y divide-border">
              {entries.map((entry) => (
                <HqNotificationEntry key={entry.id} entry={entry} onOpen={() => setOpen(false)} />
              ))}
            </ul>
          </ScrollArea>
        )}
        <Separator />
        <div className="p-2">
          <Button asChild variant="ghost" size="sm" className="w-full">
            <Link to="/bestallningar/historik" onClick={() => setOpen(false)}>
              {t("shop.hq_bell.see_all")}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function HqNotificationEntry({
  entry,
  onOpen,
}: {
  readonly entry: HqEntry;
  readonly onOpen?: () => void;
}) {
  const t = useT();
  const markRead = useMarkHqNotificationsRead();
  const Glyph = hqNotificationIcons[entry.kind as keyof typeof hqNotificationIcons];

  return (
    <li
      className={`flex gap-3 border-s-2 p-3 ${entry.read ? "border-s-transparent" : "border-s-primary"}`}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 text-muted-foreground">
        {Glyph === undefined ? null : <Glyph {...iconSize.control} />}
      </span>
      <div className="grid min-w-0 flex-1 gap-1">
        <div className="flex items-baseline justify-between gap-3">
          <Link
            to="/bestallningar/$orderId"
            params={{ orderId: entry.shop_order_id }}
            className="text-body-sm font-medium hover:underline"
            onClick={() => {
              if (!entry.read) markRead.mutate([entry.id]);
              onOpen?.();
            }}
          >
            {t(`shop.hq_bell.kind.${entry.kind}` as MessageKey)}
          </Link>
          <time
            dateTime={entry.created_at}
            className="shrink-0 text-caption tabular-nums text-muted-foreground"
          >
            {formatInstant(entry.created_at, HQ_TIMEZONE)}
          </time>
        </div>
        <p className="text-caption text-muted-foreground">
          {t("shop.hq_bell.body", {
            workshop: entry.franchise_name,
            number: String(entry.order_number),
          })}
        </p>
      </div>
    </li>
  );
}

function useOrderChime(entries: readonly HqEntry[], loaded: boolean) {
  const t = useT();
  const preferences = useNotificationPreferences();
  const newest = entries[0];
  const seen = useRef<string | null>(null);
  const primed = useRef(false);

  useEffect(() => armSound(), []);

  useEffect(() => {
    if (!loaded) return;
    if (!primed.current) {
      primed.current = true;
      seen.current = newest?.id ?? null;
      return;
    }
    if (newest === undefined) return;

    const previous = seen.current;
    seen.current = newest.id;
    if (previous === newest.id || newest.read) return;
    const settings = preferences.data?.preferences;
    if (settings?.sound_enabled !== false) {
      void (newest.kind === "shop_order_placed" ? playOrderChime() : playChime());
    }
    if (
      settings?.desktop_enabled === true &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted" &&
      document.visibilityState === "hidden"
    ) {
      new Notification(t(`shop.hq_bell.kind.${newest.kind}` as MessageKey), {
        body: t("shop.hq_bell.body", {
          workshop: newest.franchise_name,
          number: String(newest.order_number),
        }),
        tag: newest.id,
      });
    }
  }, [loaded, newest, preferences.data, t]);
}
